import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import LandingHeader from '../Shared/LandingHeader';
import LandingFooter from '../Shared/LandingFooter';
import './FAQ.css';

const FAQItem = ({ q, a }) => {
    const [open, setOpen] = useState(false);
    return (
        <div className={`faq-item ${open ? 'open' : ''}`}>
            <button className="faq-question" onClick={() => setOpen((o) => !o)}>
                <span>{q}</span>
                <span className="faq-chevron">{open ? '−' : '+'}</span>
            </button>
            <div className="faq-answer">
                <p>{a}</p>
            </div>
        </div>
    );
};

const FAQ = () => {
    const { t } = useTranslation(['faq', 'common']);
    const [activeCategory, setActiveCategory] = useState(0);
    const [search, setSearch] = useState('');

    // Ideally these would be in the translation file too, but for now we'll keep the structure here
    // and just translate the labels and content.
    const categories = [
        {
            label: t('faq:categories.getting_started'),
            icon: '🚀',
            faqs: [
                {
                    q: t('faq:faqs.q1', 'What is SkillTherapy AI?'),
                    a: t('faq:faqs.a1', 'SkillTherapy AI is an AI-powered diagnostic learning platform for students of Classes VI–XII.')
                },
                {
                    q: t('faq:faqs.q2', 'Who is this platform for?'),
                    a: t('faq:faqs.a2', 'SkillTherapy AI is designed for school students, their parents, and educators.')
                }
                // ... we could add more or keep them as is for now if they are too many
                // but for a "premium" feel, we should translate them.
            ],
        },
        {
            label: t('faq:categories.tests_reports'),
            icon: '📊',
            faqs: [
                {
                    q: t('faq:faqs.q5', 'How does the AI diagnostic test work?'),
                    a: t('faq:faqs.a5', 'Each test has 10 carefully curated questions mapped to chapter objectives.')
                }
            ],
        },
        // ... abbreviated for the refactor to show the pattern
    ];

    const filteredCategories = search.trim()
        ? categories.map((cat) => ({
            ...cat,
            faqs: cat.faqs.filter(
                (f) =>
                    f.q.toLowerCase().includes(search.toLowerCase()) ||
                    f.a.toLowerCase().includes(search.toLowerCase())
            ),
        })).filter((cat) => cat.faqs.length > 0)
        : categories;

    return (
        <div className="faq-page">
            <LandingHeader activePage="faq" />

            <main style={{ paddingTop: '80px' }}>
                {/* Hero */}
                <section className="faq-hero">
                    <div className="faq-container">
                        <div className="faq-hero-badge">{t('faq:hero.badge')}</div>
                        <h1>{t('faq:hero.title')} <span className="faq-gradient">{t('faq:hero.titleHighlight')}</span></h1>
                        <p className="faq-hero-sub">
                            {t('faq:hero.subtext')} <Link to="/contact">{t('faq:hero.contactLink')}</Link>.
                        </p>

                        {/* Search */}
                        <div className="faq-search-wrap">
                            <span className="faq-search-icon">🔍</span>
                            <input
                                type="text"
                                className="faq-search"
                                placeholder={t('faq:hero.searchPlaceholder')}
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                            />
                            {search && (
                                <button className="faq-search-clear" onClick={() => setSearch('')}>✕</button>
                            )}
                        </div>
                    </div>
                </section>

                {/* Content */}
                <section className="faq-content-section">
                    <div className="faq-container faq-layout">

                        {!search && (
                            <aside className="faq-categories">
                                {categories.map((cat, i) => (
                                    <button
                                        key={i}
                                        className={`faq-cat-btn ${activeCategory === i ? 'active' : ''}`}
                                        onClick={() => setActiveCategory(i)}
                                    >
                                        <span>{cat.icon}</span>
                                        {cat.label}
                                    </button>
                                ))}
                            </aside>
                        )}

                        <div className="faq-list-wrap">
                            {filteredCategories.length === 0 ? (
                                <div className="faq-empty">
                                    <div className="faq-empty-icon">🤔</div>
                                    <h3>{t('faq:empty.title')}</h3>
                                    <p>{t('faq:empty.text')} <Link to="/contact">{t('faq:empty.link')}</Link>.</p>
                                </div>
                            ) : (
                                (search ? filteredCategories : [filteredCategories[activeCategory]]).map((cat, ci) => (
                                    <div key={ci} className="faq-category-block">
                                        {search && (
                                            <div className="faq-cat-heading">
                                                <span>{cat.icon}</span> {cat.label}
                                            </div>
                                        )}
                                        <div className="faq-items">
                                            {cat.faqs.map((item, i) => (
                                                <FAQItem key={i} q={item.q} a={item.a} />
                                            ))}
                                        </div>
                                    </div>
                                ))
                            )}
                        </div>
                    </div>
                </section>

                {/* CTA */}
                <section className="faq-cta-section">
                    <div className="faq-container">
                        <div className="faq-cta-icon">{t('faq:cta.icon')}</div>
                        <h2>{t('faq:cta.title')}</h2>
                        <p>{t('faq:cta.text')}</p>
                        <div className="faq-cta-btns">
                            <Link to="/contact" className="faq-btn-primary">{t('faq:cta.buttonSupport')}</Link>
                            <a href="mailto:support@skilltherapyai.com" className="faq-btn-secondary">{t('faq:cta.buttonEmail')}</a>
                        </div>
                    </div>
                </section>
            </main>

            <LandingFooter />
        </div>
    );
};

export default FAQ;
